// Iter200 — How to Use MarkovLab
// Overview tab: pictorial helicopter-view workflow (numbered stages 1-4)
// + detailed numbered steps. Dedicated tabs for Position Management,
// Wheel Income, Trade Log, Scanner and Backtest Lab.
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowDown, ClipboardList, LayoutDashboard, Filter, Search,
  BookOpen, FlaskConical, TrendingUp, Repeat, Briefcase, Settings as SettingsIcon,
  UserCircle, CreditCard,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STAGES = [
  { n: 1, title: "Set Up", icon: ClipboardList, color: "#4FC3F7", pages: ["Portfolio", "Dashboard"], blurb: "Enter positions, confirm the account snapshot" },
  { n: 2, title: "Discover", icon: Filter, color: "#F59E0B", pages: ["Wheel Income", "Scanner", "Shadow Money", "Ticker Talk"], blurb: "Screen for candidates, deep-dive one ticker, read filings and headlines" },
  { n: 3, title: "Manage", icon: TrendingUp, color: "#10B981", pages: ["Position Management"], blurb: "Track lifecycle of every open position" },
  { n: 4, title: "Review & Learn", icon: BookOpen, color: "#A78BFA", pages: ["Trade Log", "Backtest Lab"], blurb: "Log outcomes, study what works — then repeat" },
];

function WorkflowDiagram() {
  return (
    <div className="border rounded-sm p-4 md:p-6 mb-10" data-testid="howto-diagram"
      style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
      <div className="text-[10px] tracking-widest opacity-60 mb-4 font-bold">THE OPERATION AT A GLANCE</div>
      <div className="flex flex-col md:flex-row items-stretch gap-2">
        {STAGES.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className="flex-1 border rounded-sm p-3 relative" data-testid={`howto-stage-${s.n}`}
              style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full flex items-center justify-center font-mono-data text-sm font-bold flex-shrink-0"
                  style={{ background: `${s.color}22`, color: s.color, border: `1px solid ${s.color}66` }}>
                  {s.n}
                </div>
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
                <span className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>{s.title}</span>
              </div>
              <div className="flex flex-wrap gap-1 mb-1.5">
                {s.pages.map(p => (
                  <span key={p} className="text-[9px] px-1.5 py-0.5 rounded-sm border font-mono-data"
                    style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>{p}</span>
                ))}
              </div>
              <p className="text-[10px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>{s.blurb}</p>
            </div>
            {i < STAGES.length - 1 && (
              <div className="flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-4 h-4 hidden md:block" style={{ color: "var(--hoip-text-tertiary)" }} />
                <ArrowDown className="w-4 h-4 md:hidden" style={{ color: "var(--hoip-text-tertiary)" }} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        <Repeat className="w-3 h-3 text-purple-400" />
        What you learn in <b>4</b> feeds back into <b>2</b> — the loop you repeat every session.
      </div>
    </div>
  );
}

function Step({ n, icon: Icon, title, children, testid }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 rounded-sm flex items-center justify-center border font-mono-data text-sm"
          style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}>
          {n}
        </div>
        <Icon className="w-4 h-4 text-blue-400" />
        <h2 className="text-base font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</h2>
      </div>
      <div className="pl-11 text-sm leading-6" style={{ color: "var(--hoip-text-secondary)" }}>
        {children}
      </div>
    </section>
  );
}

function DetailTab({ testid, icon: Icon, color, title, what, capabilities, workflow, goodToKnow }) {
  return (
    <div className="space-y-6" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5" style={{ color }} />
        <h2 className="text-lg font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</h2>
      </div>
      <p className="text-sm leading-6" style={{ color: "var(--hoip-text-secondary)" }}>{what}</p>
      <div>
        <div className="text-[10px] tracking-widest opacity-60 mb-2 font-bold">KEY CAPABILITIES</div>
        <ul className="space-y-1.5">
          {capabilities.map((c, i) => (
            <li key={i} className="flex gap-2 text-[13px] leading-6" style={{ color: "var(--hoip-text-secondary)" }}>
              <span style={{ color }}>▸</span><span>{c}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="text-[10px] tracking-widest opacity-60 mb-2 font-bold">TYPICAL WORKFLOW</div>
        <ol className="space-y-1.5">
          {workflow.map((w, i) => (
            <li key={i} className="flex gap-2 text-[13px] leading-6" style={{ color: "var(--hoip-text-secondary)" }}>
              <span className="font-mono-data flex-shrink-0" style={{ color }}>{i + 1}.</span><span>{w}</span>
            </li>
          ))}
        </ol>
      </div>
      {goodToKnow && (
        <p className="text-[12px] leading-5 border-l-2 pl-3" style={{ color: "var(--hoip-text-tertiary)", borderColor: color }}>
          {goodToKnow}
        </p>
      )}
    </div>
  );
}

const OTHER_SECTIONS = [
  { icon: LayoutDashboard, title: "Dashboard", desc: "Account snapshot (NLV, margin, buying power), Portfolio Risk Health LED, and current market-regime context." },
  { icon: Briefcase, title: "Portfolio", desc: "Single source of truth for holdings; PIE analytics, MPT/Monte Carlo, and Broker-Truth Reconciliation." },
  { icon: SettingsIcon, title: "Settings", desc: "Account type & NLV, risk limits, and strategy rules." },
  { icon: UserCircle, title: "Profile", desc: "Plan, broker connections, display name, consent history, data export, and account deletion." },
  { icon: CreditCard, title: "Plans", desc: "Trial status, paid plans, upgrades, and payment history." },
];

export default function HowToPage() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="howto-page">
      <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
        How to Use MarkovLab
      </h1>
      <p className="text-sm mb-6" style={{ color: "var(--hoip-text-secondary)" }}>
        Start with the helicopter view below, then open a tab for the tool you want to master. New here?{" "}
        <Link to="/setup" className="text-blue-400 hover:underline" data-testid="howto-setup-link">
          Broker + data setup is on its own page
        </Link>{" "}
        — you only do that once.
      </p>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-[#111111] border border-[#27272A] rounded-sm h-8 p-0.5 flex-wrap">
          <TabsTrigger value="overview" className="text-xs h-7 rounded-sm px-3" data-testid="howto-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="position" className="text-xs h-7 rounded-sm px-3" data-testid="howto-tab-position">Position Management</TabsTrigger>
          <TabsTrigger value="wheel" className="text-xs h-7 rounded-sm px-3" data-testid="howto-tab-wheel">Wheel Income</TabsTrigger>
          <TabsTrigger value="tradelog" className="text-xs h-7 rounded-sm px-3" data-testid="howto-tab-tradelog">Trade Log</TabsTrigger>
          <TabsTrigger value="scanner" className="text-xs h-7 rounded-sm px-3" data-testid="howto-tab-scanner">Scanner</TabsTrigger>
          <TabsTrigger value="shadow" className="text-xs h-7 rounded-sm px-3" data-testid="howto-tab-shadow">Shadow Money</TabsTrigger>
          <TabsTrigger value="talk" className="text-xs h-7 rounded-sm px-3" data-testid="howto-tab-talk">Ticker Talk</TabsTrigger>
          <TabsTrigger value="backtest" className="text-xs h-7 rounded-sm px-3" data-testid="howto-tab-backtest">Backtest Lab</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <WorkflowDiagram />

          <Step n={1} icon={ClipboardList} title="Set Up — Portfolio, then Dashboard" testid="howto-step-1">
            <p className="mb-2">
              Open the <b>Portfolio</b> page and fill in your positions and cost basis — stocks, options,
              and any open spreads. Prefer not to type them in? Import your broker trade file instead
              (<b>Trade Log → Import</b>, IBKR .tlg / CSV): open positions found in the file are
              reconciled into your Portfolio automatically. Either way, Portfolio is the single source
              of truth every other page reads from.
            </p>
            <p className="mb-2">
              Then confirm on the <b>Dashboard</b>: Net Liquidation Value, Excess Liquidity, Maintenance
              Margin and Buying Power should match your broker. If anything looks off, the{" "}
              <b>Broker-Truth Reconciliation</b> widget under Portfolio → PIE diffs your computed math
              against broker truth, per field.
            </p>
            <p style={{ color: "var(--hoip-text-tertiary)" }}>
              Tip: options positions carry through automatically to Wheel Income for “manage existing”
              suggestions, so fill them in accurately.
            </p>
          </Step>

          <Step n={2} icon={Filter} title="Discover — Wheel Income, then Scanner" testid="howto-step-2">
            <p className="mb-2">
              <b>Wheel Income</b> screens the ticker universe for income candidates — Cash-Secured Puts,
              Bull-Put Spreads, Jade Lizards — ranked against your target premium, DTE and minimum weekly
              yield. When something looks interesting, click through to the <b>Scanner</b> for the full
              option chain, IV context, regime detail and per-strike Greeks.
            </p>
            <p style={{ color: "var(--hoip-text-tertiary)" }}>
              Discovery happens on the Wheel page; the Scanner is the microscope.
            </p>
          </Step>

          <Step n={3} icon={TrendingUp} title="Manage — Position Management" testid="howto-step-3">
            <p className="mb-2">
              Once positions are on, <b>Position Management</b> tracks each one through its lifecycle —
              profit captured, days to expiry, distance to strike, regime fit — and classifies its stage
              (hold, take-profit zone, roll candidate, defensive review) using rules you configure in Settings.
            </p>
          </Step>

          <Step n={4} icon={BookOpen} title="Review & Learn — Trade Log and Backtest Lab" testid="howto-step-4">
            <p className="mb-2">
              Close the loop: log every outcome in the <b>Trade Log</b> (or import from your broker) and study
              the analytics — win rate by strategy, P&amp;L by regime, the Strategy × Regime cross-table.
              Use the <b>Backtest Lab</b> to test rule changes against history before applying them.
              What you learn feeds the next Discover pass.
            </p>
          </Step>

          <div className="mt-4 pt-6 border-t" style={{ borderColor: "var(--hoip-border)" }} data-testid="howto-other-sections">
            <div className="text-[10px] tracking-widest opacity-60 mb-3 font-bold">SUPPORTING SECTIONS</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {OTHER_SECTIONS.map(s => (
                <div key={s.title} className="border rounded-sm p-3 flex gap-2.5"
                  style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
                  <s.icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }} />
                  <div>
                    <div className="text-[12px] font-bold mb-0.5">{s.title}</div>
                    <p className="text-[11px] leading-5" style={{ color: "var(--hoip-text-secondary)" }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="position" className="mt-6">
          <DetailTab testid="howto-detail-position" icon={TrendingUp} color="#10B981" title="Position Management"
            what="The maintenance desk for every open position. Instead of re-analyzing each trade by hand, it continuously evaluates the state of your book — how much of the maximum profit has been captured, how many days remain to expiry, how far price sits from your strikes, and whether the current market regime still fits the position's thesis — and sorts positions into lifecycle stages."
            capabilities={[
              "Lifecycle classification per position: hold, take-profit zone, roll candidate, or defensive review — driven by thresholds you set in Settings → Strategy Rules.",
              "Profit-capture tracking: percent of max premium earned vs. time elapsed, so you can spot positions earning their keep slowly.",
              "Regime-fit check: flags positions whose entry regime has shifted (e.g., a premium-selling position now sitting in a volatile regime).",
              "Roll analysis for short options: candidate strikes/expiries with the credit or debit each roll would generate.",
              "Direct links into the Scanner for any underlying that needs a deeper look.",
            ]}
            workflow={[
              "Open the page each session after the Dashboard check — positions needing attention sort to the top.",
              "Review anything classified take-profit or defensive review first; the card shows the metrics behind the classification.",
              "For roll candidates, compare the suggested roll's credit and new strike distance before deciding.",
              "Adjust the classification thresholds under Settings → Strategy Rules if the defaults don't match how you manage trades.",
            ]}
            goodToKnow="Classifications are model-derived descriptions of position state, not directives to act. You decide what to do; the page's job is to make sure nothing slips through unnoticed." />
        </TabsContent>

        <TabsContent value="wheel" className="mt-6">
          <DetailTab testid="howto-detail-wheel" icon={Filter} color="#F59E0B" title="Wheel Income"
            what="The income-strategy engine and your discovery starting point. It screens the ticker universe for premium-selling candidates and ranks them against your criteria, then helps you assemble multiple positions into a basket that respects your portfolio-level risk limits. It also tracks complete wheel cycles — put → assignment → covered call — end to end."
            capabilities={[
              "Candidate screening for Cash-Secured Puts, Bull-Put Spreads and Jade Lizards with target premium, DTE window and minimum weekly-yield filters.",
              "Strike-selection engines: choose regime-scaled delta, key-level, or minimum P(non-touch) (PNT) filtering per ticker.",
              "Per-candidate metrics: premium, weekly yield, POP / POT, margin requirement, regime fit and strategy score.",
              "Basket builder: assembles a multi-position basket under sector caps, per-ticker caps and max margin utilization from your Risk Limits.",
              "Existing-position handling: evaluates open income trades for take-profit, roll or close conditions.",
              "Wheel-cycle tracking: links assigned puts and subsequent covered calls into one cycle with cumulative P&L.",
            ]}
            workflow={[
              "Set your filters (premium, DTE, yield floor, P(non-touch)) — they persist between sessions.",
              "Scan and review the ranked candidates; the 'Hide thin/illiquid options' toggle is on by default — expand the 'N hidden' drawer if something you expected is missing.",
              "Shortlist candidates, then open the Scanner on each for the full-chain deep dive.",
              "Use the basket builder to check the combined margin and concentration impact before committing.",
            ]}
            goodToKnow="Candidates are ranked by your configured criteria and model outputs — a high rank describes screen fit, not a recommendation. Probability figures are model estimates, not guarantees." />
        </TabsContent>

        <TabsContent value="tradelog" className="mt-6">
          <DetailTab testid="howto-detail-tradelog" icon={BookOpen} color="#A78BFA" title="Trade Log"
            what="Your trade history and the feedback half of the loop. Every closed trade becomes data: each entry is tagged with the market regime at entry, so the analytics can tell you not just how a strategy performed, but under which conditions it performed."
            capabilities={[
              "Manual logging with strategy, strikes, expiry, premium direction and outcome notes.",
              "Broker import: upload a broker trade log (supported IBKR formats — .tlg, Activity Statement, Flex Query). Imports are new entries only, deduplicated by execution ID; account identifiers and filenames are not stored. Open positions found in the file are synced into your Portfolio automatically — manually entered positions are never duplicated. One-click undo removes a mistaken import batch together with its synced positions.",
              "Regime-at-entry tagging on every trade (with confidence), plus a backfill tool for historical entries.",
              "Analytics: equity curve, win rate by strategy, P&L by regime, and the Strategy × Regime cross-table showing whether a strategy's edge holds across market conditions.",
              "Customizable grid: ticker search, sortable columns, drag-to-reorder headers, show/hide columns — layout persists per session.",
              "Full CSV export any time from the Profile page.",
            ]}
            workflow={[
              "Log (or import) each trade when you open it — the regime tag is captured automatically at entry.",
              "Close trades with an exit reason (expired, assigned, closed early) so the analytics stay meaningful.",
              "Visit the Analytics tab monthly: check whether win rates concentrate in one regime before scaling a strategy.",
              "Use 'Backfill regime' after importing history so older trades join the regime analytics.",
            ]}
            goodToKnow="Past results describe your history; they don't determine future outcomes. The Strategy × Regime table is most useful once a cell has a meaningful sample size — read small counts with caution." />
        </TabsContent>

        <TabsContent value="scanner" className="mt-6">
          <DetailTab testid="howto-detail-scanner" icon={Search} color="#4FC3F7" title="Scanner"
            what="The single-ticker microscope. Once discovery (Wheel Income) produces a shortlist, the Scanner is where you evaluate one underlying in full depth — chain, volatility, regime, unusual activity and historical strategy behavior, all in one workbench."
            capabilities={[
              "Full option chain with per-strike Greeks, bid/ask, volume, open interest and liquidity flags.",
              "Implied-volatility context: current IV vs. its own history, so you can see whether premium is rich or thin for that name.",
              "HMM regime timeline: the underlying's trend/volatility state history and the model's current read with confidence.",
              "Strike suggestions always use listed chain strikes (CSP/BPS round down, covered calls round up), with POP and POT on the selected structure.",
              "Unusual Options Activity panel: volume/OI outliers and IV moves, with matching chain rows highlighted.",
              "Backtest context: how comparable setups on this ticker behaved historically, drawn from the same canonical population as the Backtest Lab.",
            ]}
            workflow={[
              "Arrive from a Wheel Income candidate (or type a ticker) — the chain loads with your default expiry window.",
              "Check the regime timeline and IV context first: they frame whether any strategy family makes sense here.",
              "Inspect the specific strikes you're considering — Greeks, liquidity, and P(non-touch) at your strike.",
              "Cross-check the UOA panel and backtest context before deciding; log the trade if you proceed.",
            ]}
            goodToKnow="Scanner is deliberately a focused tool, not a discovery tool. If you find yourself typing many tickers cold, go back to Wheel Income and let the screen do the first pass." />
        </TabsContent>

        <TabsContent value="shadow" className="mt-6">
          <DetailTab testid="howto-detail-shadow" icon={Search} color="#F59E0B" title="Shadow Money"
            what="A filings tape for the Scanner universe: insider Form 4 open-market activity, a capped 13F holder snapshot, Congress disclosures, and Form 144 proposed sales. The cron writes a slim Mongo document per ticker; the page never calls the vendor."
            capabilities={[
              "Insider 30-day open-market buy/sell counts (grant/award codes excluded).",
              "Top 13F holders from one API page (≤100 rows summarized to 8). The full 13F book is not stored.",
              "Recent Senate and House transactions and Form 144 proposed sales, capped at eight rows each.",
              "Same snapshot is shown as a strip on the ticker workbench.",
            ]}
            workflow={[
              "After the house-feed cron has run, open Shadow Money and scan the table for unusual insider or Congress activity.",
              "Click a row for the holder list, then jump to the ticker workbench if you want chain/regime context.",
              "Treat 13F as lagged (~45 days) context, not a live order-flow feed.",
            ]}
            goodToKnow="Informational only. Filings can be delayed or amended. This is not a recommendation to copy any insider, fund, or member of Congress." />
        </TabsContent>

        <TabsContent value="talk" className="mt-6">
          <DetailTab testid="howto-detail-talk" icon={Search} color="#60A5FA" title="Ticker Talk"
            what="Headlines and company press releases for the Scanner universe, cached in Mongo. Sentiment uses financialdata.net metadata when the API sends it; otherwise a small headline keyword tag. No local language model runs on the server."
            capabilities={[
              "Market news bucketed onto tickers plus the latest press release, truncated to a short summary.",
              "Tone chip per ticker (constructive / cautious / mixed / quiet) derived from those items.",
              "Workbench strip so you see the latest headline without leaving the chain.",
            ]}
            workflow={[
              "Skim Ticker Talk after the cache cycle for names already on your shortlist — it is not a discovery screen.",
              "Open the row to read the cached summaries, then confirm on the workbench before acting.",
            ]}
            goodToKnow="Headlines are not advice. Keyword tags are a fallback when the vendor does not send sentiment — they are not a model of market mood." />
        </TabsContent>

        <TabsContent value="backtest" className="mt-6">
          <DetailTab testid="howto-detail-backtest" icon={FlaskConical} color="#EF4444" title="Backtest Lab"
            what="Historical strategy evaluation. Before changing your rules — a different DTE window, a tighter delta, a new profit-take threshold — the Backtest Lab lets you see how that rule set would have behaved against historical option-chain and price data."
            capabilities={[
              "Strategy templates run against historical chains for a chosen ticker and window.",
              "Outcome distributions: win rate, average P&L, worst drawdown and per-trade detail for the tested rules.",
              "One canonical trade population: the same backtest engine and population feeds the Scanner's context badges, so numbers agree everywhere in the app.",
              "Side-by-side comparison of rule variants to see which parameter actually drives the difference.",
            ]}
            workflow={[
              "Pick the ticker and strategy template you actually trade — testing what you don't trade wastes the signal.",
              "Run the current rule set first as the baseline.",
              "Change one parameter at a time and re-run; compare distributions, not just the headline win rate.",
              "Cross-reference the Trade Log's regime analytics: a rule that only worked in one regime deserves skepticism.",
            ]}
            goodToKnow="Backtests describe how a rule set behaved in past data under simplifying assumptions (fills, spreads, no slippage modeling beyond mid/bid choices). Past performance does not determine future results." />
        </TabsContent>
      </Tabs>

      <div className="mt-10 pt-6 border-t text-xs"
        style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}
        data-testid="howto-disclaimer">
        MarkovLab provides analytics and decision-support tools for self-directed investors. It does not provide
        investment advice; classifications and scores are model-derived descriptions, not recommendations. You are
        responsible for your own investment decisions.
      </div>
    </div>
  );
}
